import StatusTag from '@/components/_common/StatusTag';
import { useWorkorderValidateImportReplace } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.hooks';
import { ThereIsWorkOrder } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import { ErrorFieldErrors } from '@/core/request/ErrorResponse';
import { WorkorderImportInfo } from '@/pages/MovementsPage/WorkOrdersPage/WorkOrderImportPage';
import { buildParamsStr } from '@/utils/queryParams';
import {
  Alert,
  Button,
  Col,
  Divider,
  List,
  Modal,
  ModalProps,
  Row,
  Table,
  Typography,
} from 'antd';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

interface Props extends ModalProps {
  onSubmit: (type: WorkorderImportInfo) => void;
  workorders?: ThereIsWorkOrder[];
  companyId?: number;
}

const WorkorderImportInfoModal: React.FC<Props> = ({
  onSubmit,
  workorders,
  companyId,
  ...props
}) => {
  const history = useHistory();
  const [workorderSelected, setWorkorderSelected] = useState<{
    workorderId: number;
    complementId: number;
  }>();

  const validateImportReplace = useWorkorderValidateImportReplace();

  const redirectToReturnMaterialPage = () => {
    const workorder = workorders?.find(
      (item) => item.workOrderId === workorderSelected?.workorderId,
    );

    if (workorder && companyId) {
      const search = buildParamsStr({
        companyId,
        search: workorder?.workOrderNumber,
      });

      window.open(
        `/app/movements/deliveryOfMaterials/returnMaterial?${search}`,
        '_blank',
      );
    }
  };

  async function validateWorkorderReplace() {
    try {
      if (workorderSelected?.complementId) {
        await validateImportReplace.mutateAsync(
          workorderSelected?.complementId,
        );

        onSubmit({ ...workorderSelected, importAs: 2 });
      }
    } catch (error) {
      const err = error as AxiosError<ErrorFieldErrors>;

      if (
        err.isAxiosError &&
        err.response?.data &&
        err.response?.data?.fieldErrors &&
        err.response.status === 400
      ) {
        const { response } = err;

        Modal.info({
          icon: null,
          title: 'Não é possível substituir o orçamento',
          okText: 'Confirmar',
          width: 800,
          content: (
            <>
              {!!response.data.fieldErrors?.invoice?.length && (
                <Alert
                  style={{ marginTop: 24 }}
                  type="warning"
                  message={<strong>Nota Fiscal</strong>}
                  description="Existe nota fiscal, faça a exclusão ou cancelamento antes de
                              cancelar a O.S."
                />
              )}

              {!!response.data.fieldErrors?.financial?.length && (
                <Alert
                  style={{ marginTop: 24 }}
                  type="warning"
                  message={<strong>Financeiro</strong>}
                  description="Existe financeiro referente a este orçamento."
                />
              )}

              {!!response.data.fieldErrors?.purchase?.length && (
                <>
                  <Divider orientation="left" style={{ fontWeight: 'bold' }}>
                    Existem compra(s) referentes aos itens
                  </Divider>

                  <List
                    bordered
                    dataSource={response.data.fieldErrors?.purchase}
                    renderItem={(item) => <List.Item>{item}</List.Item>}
                  />
                </>
              )}

              {!!response.data.fieldErrors?.returnedItems?.length && (
                <>
                  <Divider orientation="left" style={{ fontWeight: 'bold' }}>
                    Itens a serem devolvidos
                  </Divider>

                  <List
                    bordered
                    dataSource={response.data.fieldErrors.returnedItems}
                    renderItem={(item) => <List.Item>{item}</List.Item>}
                    footer={
                      <Button
                        type="link"
                        onClick={redirectToReturnMaterialPage}
                      >
                        Fazer devolução dos itens
                      </Button>
                    }
                  />
                </>
              )}
            </>
          ),
        });
      }
    }
  }

  return (
    <Modal
      {...props}
      footer={null}
      title="Já existe orçamento para este veículo!"
      width={900}
      closable={false}
    >
      <Typography.Text>
        Selecione uma O.S. para substituir/adicionar como complemento, ou clique
        em adicionar como novo.
      </Typography.Text>

      <Table<ThereIsWorkOrder>
        style={{ marginTop: 24 }}
        dataSource={workorders}
        pagination={false}
        columns={[
          {
            dataIndex: 'workOrderNumber',
            title: 'Orçamento',
          },
          {
            dataIndex: 'customer',
            title: 'Cliente',
          },
          {
            dataIndex: 'situation',
            title: 'Situação',
            render: (status: 'ABERTO' | 'AUTORIZADO') => (
              <StatusTag
                color={status === 'ABERTO' ? 'blue' : 'yellow'}
                label={status === 'ABERTO' ? 'Aberto' : 'Autorizado'}
              />
            ),
          },
        ]}
        rowKey={(row) => `${row.workOrderId}.${row.workOrderComplementId}`}
        rowSelection={{
          preserveSelectedRowKeys: true,
          type: 'radio',
          onChange: (keys) => {
            const ids = keys[0] ? (keys[0] as string).split('.') : [];

            if (ids.length) {
              setWorkorderSelected({
                workorderId: Number(ids[0]),
                complementId: Number(ids[1]),
              });
            }
          },
        }}
      />

      <Divider />

      <Row gutter={[16, 16]}>
        <Col>
          <Button
            danger
            type="primary"
            onClick={() => history.push('/app/movements/workOrders')}
          >
            Cancelar
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={() => onSubmit({ ...workorderSelected, importAs: 0 })}
          >
            Adicionar como novo
          </Button>
        </Col>
        <Col>
          <Button
            disabled={!workorderSelected}
            onClick={validateWorkorderReplace}
            loading={validateImportReplace.isLoading}
          >
            Substituir
          </Button>
        </Col>
        <Col>
          <Button
            disabled={!workorderSelected}
            onClick={() => onSubmit({ ...workorderSelected, importAs: 1 })}
          >
            Adicionar como complemento
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default WorkorderImportInfoModal;
